import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';

import BaseModal from 'components/shared/Modal/BaseModal';
import FormattedInput from 'components/shared/FormattedInput/FormattedInput';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import Checkbox from 'components/shared/Checkbox/Checkbox';
import {
  getSmsActivationCode,
  verifySmsActivationCode,
  submitSmsActivation,
  updateAlertsAndNotifications
} from 'services/Rest';
import Content, { contentConverter } from 'services/Content';
import {
  getActivationCode,
  getMobileCarriers,
  retrieveAlertsAndNotifications
} from 'state/alertsAndNotifications/AlertsAndNotificationsSelectors';
import { ReactComponent as ErrorMessageImage } from 'assets/error-message.svg';
import { ReactComponent as AllSetImage } from 'assets/alertsAndNotifications/allSet.svg';
import SaveLoadingButtonWithPermissions from 'components/shared/LoadingButton/SaveLoadingButtonWithPermissions';
import {
  NON_BREAKING_SPACE,
  maskPhoneNumber,
  formatReadOnlyPhoneNumber
} from 'services/Formats';
import { REQUEST_STATES } from 'state/requests/RequestStatusReducer';
import { getRequestStatus } from 'state/requests/RequestStatusSelectors';
import LabeledSelect from 'components/shared/SelectOption/LabeledSelect';
import { simplePhoneNumberToAppPhoneNumber } from 'services/Preferences';
import { hasAlaska529Only, hasMaryland529Only, hasNational529Only } from 'state/view/ViewSelectors';

const PhoneNumber = ({ phoneNumber }) => (
  <span>{formatReadOnlyPhoneNumber(phoneNumber)}</span>
);

const PhoneNumberWithPermissions = WithUserPermissions(PhoneNumber);
const CheckboxWithPermissions = WithUserPermissions(Checkbox);

export const createAllSMSPreferences = (alertPreferences) => {
  return alertPreferences
    .filter((a) => a.businessLineId === 2)
    .map((al) => {
      const copiedAlert = {
        ...al
      };
      copiedAlert.optedOut = false;
      copiedAlert.contactMethods = [
        {
          activityAlertContactMethodType: 'Email',
          activityAlertContactMethodTypeId: 260
        },
        {
          activityAlertContactMethodType: 'SMS',
          activityAlertContactMethodTypeId: 259
        }
      ];
      return copiedAlert;
    });
};

export class AlertsAndNotificationsModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      step: 1,
      agreeToTerms: false,
      isErrorActivationCode: false,
      isValidActivationCode: true,
      activationCode: '',
      mobileCarrier: 0
    };
  }

  componentDidUpdate(prevProps) {
    const { requestStatus } = this.props;
    const { requestStatus: prevRequest } = prevProps;

    if (
      requestStatus.sendSmsActivationCode === REQUEST_STATES.error &&
      prevRequest.sendSmsActivationCode !== REQUEST_STATES.error
    ) {
      this.setState({ isErrorActivationCode: true });
    }
  }

  setStep = (step) => {
    this.setState({ step: step });
  };

  setActivationCode = (value) => {
    this.setState({ activationCode: value });
  };

  handleResendCode = () => {
    const { mobileCarrier } = this.state;
    const { mobileCarriers, phoneNumber } = this.props;

    const completeMobileCarrier =
      mobileCarriers &&
      mobileCarriers.find(
        (carrier) => carrier.value === parseInt(mobileCarrier)
      );
    const payload = {
      ...simplePhoneNumberToAppPhoneNumber(phoneNumber),
      mobileCarrier: {
        mobileCarrierId: completeMobileCarrier.value,
        mobileCarrierName: completeMobileCarrier.label
      }
    };

    getSmsActivationCode(payload);
    this.setState({ isValidActivationCode: true });
  };

  setAgreeToTerms = () => {
    const { agreeToTerms } = this.state;

    this.setState({ agreeToTerms: !agreeToTerms });
  };

  sendActivationCode = () => {
    const { mobileCarrier } = this.state;
    const { mobileCarriers, phoneNumber } = this.props;

    const completeMobileCarrier =
      mobileCarriers &&
      mobileCarriers.find(
        (carrier) => carrier.value === parseInt(mobileCarrier)
      );
    const payload = {
      ...simplePhoneNumberToAppPhoneNumber(phoneNumber),
      mobileCarrier: {
        mobileCarrierId: completeMobileCarrier.value,
        mobileCarrierName: completeMobileCarrier.label
      }
    };
    getSmsActivationCode(payload, () => {
      this.setStep(2);
    });
  };

  shouldDisableActivationCode = () => {
    const { agreeToTerms, mobileCarrier } = this.state;

    return !agreeToTerms || mobileCarrier === 0;
  };

  submitActivationCode = () => {
    const { activationCode, mobileCarrier } = this.state;
    const { mobileCarriers, phoneNumber, alertPreferences } = this.props;

    const completeMobileCarrier =
      mobileCarriers &&
      mobileCarriers.find(
        (carrier) => carrier.value === parseInt(mobileCarrier)
      );
    const payload = {
      ...simplePhoneNumberToAppPhoneNumber(phoneNumber),
      mobileCarrier: {
        mobileCarrierId: completeMobileCarrier.value,
        mobileCarrierName: completeMobileCarrier.label
      },
      activationPin: activationCode
    };

    verifySmsActivationCode(
      activationCode,
      () => {
        this.inValidOtpToken();
      },
      () => {
        submitSmsActivation(payload, () => {
          // default all retail to SMS
          const updatedAlerts = createAllSMSPreferences(alertPreferences);
          updateAlertsAndNotifications(updatedAlerts, true);
          this.setState({ isValidActivationCode: true });
          this.setStep(3);
        });
      }
    );
  };

  inValidOtpToken() {
    this.setState({ isValidActivationCode: false });
  }

  handleCarrierChange = (event) => {
    this.setState({ mobileCarrier: event.target.value });
  };

  closeModal = () => {
    const { handleCloseModal } = this.props;
    const { step } = this.state;
    const refreshData = step === 3;
    handleCloseModal(refreshData);
  };

  render() {
    const { handleCloseModal, isAlaska529Only, isMaryland529Only, isNational529Only, mobileCarriers, phoneNumber } = this.props;
    const {
      step,
      agreeToTerms,
      activationCode,
      isErrorActivationCode,
      isValidActivationCode,
      mobileCarrier
    } = this.state;


    const isPostOneTrustLaunch = () => {
      const launchDateandTimeString = '2023-08-30T20:00:00Z';
      const currentDate = new Date();
      const launchDate = new Date(launchDateandTimeString);
      return currentDate > launchDate;
    };

    const footerText = isPostOneTrustLaunch() && !(isAlaska529Only || isMaryland529Only || isNational529Only) ? contentConverter.makeHtml(
      Content.alertsAndNotificationsLegalFooter
    ) :contentConverter.makeHtml(
      Content.alertsAndNotificationsFooter
    );

    const isAnyError = isErrorActivationCode;
    const disableSendCode = this.shouldDisableActivationCode();
    const disableSubmit = activationCode.length < 6;

    const tokenValidationInputClassName = classnames(
      'alerts-and-notifications-modal-input-activation',
      { 'validation-error': !isValidActivationCode }
    );

    if (isAnyError) {
      return (
        <BaseModal
          containerClassName="alerts-and-notifications-modal-container"
          disableClickOff
          hideAnchor
          hideClose
          isOpen
          title=" ">
          <div
            data-testid="alerts-and-notifications-error-modal"
            className="alerts-and-notifications-modal-error-icon-container">
            <ErrorMessageImage />
            <div className="alerts-and-notifications-modal-error-heading">
              We are having technical issues setting up your activity alerts.
            </div>
            <div className="alerts-and-notifications-modal-error-separator" />
            <div className="alerts-and-notifications-modal-error-sub-text">
              Please try again later.
            </div>
            <button
              className="alerts-and-notifications-modal-error-button"
              onClick={handleCloseModal}>
              Close
            </button>
          </div>
        </BaseModal>
      );
    }
    return (
      <BaseModal
        containerClassName="alerts-and-notifications-modal-container"
        close={this.closeModal}
        disableClickOff
        hideClose={step === 3}
        hideAnchor
        isOpen
        title={step !== 3 ? 'Sign Up For Text Message Alerts' : ' '}>
        <div data-testid="alert-and-notifications-modal-container">
          {step === 1 && (
            <Fragment>
              <div className="alerts-and-notifications-modal-sub-heading">
                Enter mobile number and carrier and accept the text message
                agreement to receive the activation code for text alerts.
              </div>
              <div className="mobile-carrier-select-container">
                <LabeledSelect
                  label="Mobile Carrier"
                  onChange={this.handleCarrierChange}
                  options={mobileCarriers}
                  selection={mobileCarrier}
                />
              </div>
              <div className="alerts-and-notifications-modal-input-heading">
                Mobile Number
              </div>
              <div className="alerts-and-notifications-read-only-phone">
                <PhoneNumberWithPermissions
                  phoneNumber={phoneNumber}
                  shouldMask
                />
              </div>
              <div className="alerts-and-notifications-checkbox-container">
                <CheckboxWithPermissions
                  checked={agreeToTerms}
                  className="alerts-and-notifications-modal-checkbox"
                  name="agreeToTerms"
                  labelRight={true}
                  onChange={this.setAgreeToTerms}
                  shouldDisable
                />
                I accept the{NON_BREAKING_SPACE}
                <a
                  rel="noreferrer"
                  className="text-message-agreement"
                  href="https://www.troweprice.com/personal-investing/help/policies-and-security/text-message-agreement.html"
                  target="_blank">
                  Text Message Agreement
                </a>
              </div>
              <SaveLoadingButtonWithPermissions
                className="alerts-and-notifications-modal-button"
                disabled={disableSendCode}
                id="step-1-send-activation-code-button"
                dataTestId={'step-1-send-activation-code-button'}
                onClick={this.sendActivationCode}
                requestStatusKey="sendSmsActivationCode"
                type="primary"
                shouldDisable>
                Send Activation Code
              </SaveLoadingButtonWithPermissions>
            </Fragment>
          )}
          {step === 2 && (
            <Fragment>
              <div className="alerts-and-notifications-modal-sub-heading">
                Enter the activation code sent to {maskPhoneNumber(phoneNumber)}{' '}
                to start receiving text message alerts.
              </div>
              <div className="alerts-and-notifications-modal-note">
                <strong>Please note:</strong> You are submitting to set up
                account activity alerts.
              </div>
              <FormattedInput
                className={tokenValidationInputClassName}
                format={'otpToken'}
                onBlur={() => {}}
                onChange={this.setActivationCode}
                disable={false}
                value={activationCode}
              />
              {!isValidActivationCode && (
                <div className="alerts-and-notifications-modal-validation-note">
                  The code you entered is incorrect or expired
                </div>
              )}
              {isValidActivationCode && (
                <div className="alerts-and-notifications-modal-note">
                  This one-time code expires 5 minutes after it was sent.
                </div>
              )}
              <SaveLoadingButtonWithPermissions
                className="alerts-and-notifications-resend-code"
                disabled={disableSendCode}
                id="resend-send-code-button"
                onClick={this.handleResendCode}
                requestStatusKey="sendSmsActivationCode"
                type="primary"
                shouldDisable>
                Resend Code
              </SaveLoadingButtonWithPermissions>
              <div className="alerts-and-notifications-modal-button-container">
                <SaveLoadingButtonWithPermissions
                  className="alerts-and-notifications-modal-button-submit"
                  onClick={this.submitActivationCode}
                  requestStatusKey="verifySmsActivationCode"
                  disabled={disableSubmit}
                  test-dataid={'step-2-submit-text-alerts'}
                  id="step-2-submit-text-alerts"
                  type="primary"
                  shouldDisable>
                  Submit
                </SaveLoadingButtonWithPermissions>
                <button
                  className="alerts-and-notifications-resend-code"
                  onClick={this.closeModal}>
                  Cancel
                </button>
              </div>
            </Fragment>
          )}
          {step === 3 && (
            <div className="alerts-and-notifications-success-container">
              <AllSetImage />
              <div className="alerts-and-notifications-success-header">
                All Set!
              </div>
              <div className="alerts-and-notifications-modal-error-separator" />
              <div className="alerts-and-notifications-modal-success-sub-heading">
                You are now eligible to receive text alerts. Your next step is
                to select the delivery method for each Account Activity Alert.
              </div>
              <button
                className="alerts-and-notifications-success-close"
                onClick={this.closeModal}>
                Close
              </button>
              <div className="alerts-and-notifications-footer">
                <span dangerouslySetInnerHTML={{ __html: footerText }} />
              </div>
            </div>
          )}
        </div>
      </BaseModal>
    );
  }
}

const mapStateToProps = (state) => ({
  alertPreferences: retrieveAlertsAndNotifications(state),
  activationCode: getActivationCode(state),
  requestStatus: getRequestStatus(state),
  mobileCarriers: getMobileCarriers(state),
  isMaryland529Only: hasMaryland529Only(state),
  isAlaska529Only: hasAlaska529Only(state),
  isNational529Only: hasNational529Only(state)
});

export default connect(mapStateToProps)(AlertsAndNotificationsModal);
